import React, { useState, useEffect, useCallback, useMemo } from "react";
import { adminApiRequest } from "../utils/adminApi";
import AccordionSection from "./AccordionSection";
import IngredientsTable from "./IngredientsTable";

const InventoryTab = () => {
  // ── Core state ──────────────────────────────────
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [categories, setCategories] = useState([]);
  const [infoModalStatus, setInfoModalStatus] = useState(null);
  const [editMode, setEditMode] = useState(null);
  const [resetModalItem, setResetModalItem] = useState(null);
  const [resetNewAvailable, setResetNewAvailable] = useState("0");
  const [addStockModalItem, setAddStockModalItem] = useState(null);
  const [addStockQuantity, setAddStockQuantity] = useState("");
  const [addStockExpiry, setAddStockExpiry] = useState("");
  const [addStockHasExpiry, setAddStockHasExpiry] = useState(false);
  const [editForm, setEditForm] = useState({
    inventory_available: 0,
    inventory_expired: 0,
    inventory_house: 0,
    inventory_broken: 0,
    name: "",
    quantity_used: 0,
  });
  const [submitting, setSubmitting] = useState(false);

  const [addHouseModalItem, setAddHouseModalItem] = useState(null);
  const [addHouseQuantity, setAddHouseQuantity] = useState("");
  const [addHouseApprovedBy, setAddHouseApprovedBy] = useState("");
  const [addHouseNotes, setAddHouseNotes] = useState("");

  const [viewHouseLogsItem, setViewHouseLogsItem] = useState(null);
  const [houseLogs, setHouseLogs] = useState([]);
  const [loadingHouseLogs, setLoadingHouseLogs] = useState(false);

  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showBulkAddModal, setShowBulkAddModal] = useState(false);
  const [showBulkResetModal, setShowBulkResetModal] = useState(false);
  const [bulkQuantity, setBulkQuantity] = useState("");
  const [bulkExpiry, setBulkExpiry] = useState("");
  const [bulkAddHasExpiry, setBulkAddHasExpiry] = useState(false);

  const [viewMode, setViewMode] = useState("live");
  const [historyItems, setHistoryItems] = useState([]);
  const [historyStartDate, setHistoryStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split("T")[0];
  });
  const [historyEndDate, setHistoryEndDate] = useState(() => {
    return new Date().toISOString().split("T")[0];
  });
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [ingredientsHistory, setIngredientsHistory] = useState([]);
  const [loadingIngredientsHistory, setLoadingIngredientsHistory] = useState(false);

  // ── Accordion & Ingredient state ─────────────────
  const [openSections, setOpenSections] = useState({
    food: false,
    drinks: false,
    ingredients: false,
    historyFood: false,
    historyDrinks: false,
    historyIngredients: false,
  });
  const [ingredients, setIngredients] = useState([]);
  const [loadingIngredients, setLoadingIngredients] = useState(false);
  const [ingredientSearchTerm, setIngredientSearchTerm] = useState("");
  const [showAddIngredientModal, setShowAddIngredientModal] = useState(false);
  const [ingredientForm, setIngredientForm] = useState({ name: "", quantity: "", unit: "" });
  const [showEditIngredientModal, setShowEditIngredientModal] = useState(false);
  const [editIngredientForm, setEditIngredientForm] = useState({ id: "", name: "", unit: "" });
  const [editIngredientOriginal, setEditIngredientOriginal] = useState(null);
  const [submittingIngredient, setSubmittingIngredient] = useState(false);

  const [addIngredientStockItem, setAddIngredientStockItem] = useState(null);
  const [addIngredientStockQuantity, setAddIngredientStockQuantity] = useState("");
  const [submittingAddIngredientStock, setSubmittingAddIngredientStock] = useState(false);

  const [resetIngredientItem, setResetIngredientItem] = useState(null);
  const [resetIngredientNewQty, setResetIngredientNewQty] = useState("0");
  const [submittingResetIngredient, setSubmittingResetIngredient] = useState(false);

  const [deleteIngredientItem, setDeleteIngredientItem] = useState(null);
  const [submittingDeleteIngredient, setSubmittingDeleteIngredient] = useState(false);

  // ── Data fetching ────────────────────────────────
  const fetchIngredientsHistory = useCallback(async () => {
    setLoadingIngredientsHistory(true);
    try {
      const formData = new FormData();
      formData.append("start_date", historyStartDate);
      formData.append("end_date", historyEndDate);
      const res = await adminApiRequest({
        script: "fetch-ingredients-history",
        formData,
      });
      if (res?.success) {
        setIngredientsHistory(res.data || []);
      } else {
        throw new Error(res?.error || "Failed to load ingredients history");
      }
    } catch (err) {
      console.error(err.message);
    } finally {
      setLoadingIngredientsHistory(false);
    }
  }, [historyStartDate, historyEndDate]);

  const fetchAggregatedHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const formData = new FormData();
      formData.append("start_date", historyStartDate);
      formData.append("end_date", historyEndDate);

      const res = await adminApiRequest({
        script: "fetch-aggregated-history",
        formData,
      });
      if (res?.success) {
        setHistoryItems(res.data || []);
      } else {
        throw new Error(res?.error || "Failed to load history");
      }
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoadingHistory(false);
    }
  }, [historyStartDate, historyEndDate]);

  const fetchInventory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApiRequest({
        script: "fetch-inventory",
        data: {},
      });
      if (res?.success) {
        setItems(res.data || []);
      } else {
        throw new Error(res?.error || "Failed to load inventory");
      }
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchInventorySilently = useCallback(async () => {
    try {
      const res = await adminApiRequest({
        script: "fetch-inventory",
        data: {},
      });
      if (res?.success) {
        setItems(res.data || []);
      }
    } catch (err) {
      // Silently fail on background poll
    }
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await adminApiRequest({ script: "categories", data: {} });
      if (res?.success && Array.isArray(res.data)) {
        setCategories(res.data);
      }
    } catch (err) {
      console.error("Error fetching categories:", err?.message);
    }
  };

  const fetchIngredients = useCallback(async () => {
    setLoadingIngredients(true);
    try {
      const res = await adminApiRequest({ script: "fetch-ingredients", data: {} });
      if (res?.success) {
        setIngredients(res.data || []);
      } else {
        throw new Error(res?.error || "Failed to load ingredients");
      }
    } catch (err) {
      console.error("Error fetching ingredients:", err?.message);
    } finally {
      setLoadingIngredients(false);
    }
  }, []);

  // ── Effects ──────────────────────────────────────
  useEffect(() => {
    fetchInventory();
    fetchCategories();
    fetchIngredients();
  }, [fetchInventory, fetchIngredients]);

  useEffect(() => {
    if (viewMode === "history") {
      fetchAggregatedHistory();
      fetchIngredientsHistory();
    }
  }, [viewMode, fetchAggregatedHistory, fetchIngredientsHistory]);

  useEffect(() => {
    let intervalId;
    if (viewMode === "live" && !editMode) {
      intervalId = setInterval(() => {
        fetchInventorySilently();
      }, 15000); // Poll every 15 seconds
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [viewMode, editMode, fetchInventorySilently]);

  // ── Handlers: existing inventory ─────────────────
  const handleEditClick = (item, field) => {
    if (field === "name") {
      setEditIngredientForm({ id: item.id, name: item.name || "", unit: item.unit || "" });
      setEditIngredientOriginal(item);
      setShowEditIngredientModal(true);
      return;
    }
    setEditMode({ id: item.id, field });
    if (field === "used") {
      // Ingredient inline editing
      setEditForm({
        quantity_used: Math.floor(item.quantity_used || 0),
      });
    } else {
      // Existing inventory inline editing
      setEditForm({
        inventory_available: item.inventory_available,
        inventory_expired: item.inventory_expired,
        inventory_house: item.inventory_house,
        inventory_broken: item.inventory_broken || 0,
      });
    }
  };

  const handleCancelEdit = () => {
    setEditMode(null);
  };

  const handleFormChange = (e) => {
    setEditForm({
      ...editForm,
      [e.target.name]: e.target.value,
    });
  };

  const handleEditIngredientModalSubmit = async (e) => {
    e.preventDefault();
    if (!editIngredientOriginal) return;

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("id", editIngredientForm.id);
      formData.append("name", editIngredientForm.name.trim());
      formData.append("unit", editIngredientForm.unit.trim());
      formData.append("quantity", editIngredientOriginal.total_stock - editIngredientOriginal.quantity_used);

      const res = await adminApiRequest({
        script: "update-ingredient",
        formData,
      });

      if (res?.success) {
        setSuccessMsg("Ingredient updated!");
        setTimeout(() => setSuccessMsg(""), 3000);
        setShowEditIngredientModal(false);
        fetchIngredients();
      } else {
        throw new Error(res?.error || "Failed to update ingredient");
      }
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(""), 3000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!editMode) return;

    if (editMode.field === "used") {
      // Save Ingredient inline editing changes
      setSubmitting(true);
      try {
        const currentIng = ingredients.find((i) => i.id === editMode.id);
        if (!currentIng) return;

        const formData = new FormData();
        formData.append("id", editMode.id);
        formData.append("unit", currentIng.unit);

        if (editMode.field === "used") {
          const newQtyUsed = parseFloat(editForm.quantity_used);
          if (isNaN(newQtyUsed) || newQtyUsed < 0) {
            throw new Error("Invalid quantity used entered");
          }
          if (newQtyUsed > currentIng.total_stock) {
            throw new Error("Quantity used cannot exceed total stock");
          }

          // Use the use-ingredient script but mapping the difference or directly saving new values
          const diff = newQtyUsed - currentIng.quantity_used;
          if (diff === 0) {
            setEditMode(null);
            return;
          }

          const useFormData = new FormData();
          useFormData.append("id", editMode.id);
          
          if (diff > 0) {
            useFormData.append("quantity_to_use", diff);
            const res = await adminApiRequest({
              script: "use-ingredient",
              formData: useFormData,
            });
            if (!res?.success) throw new Error(res?.error || "Failed to log usage");
          } else {
            // If user reduced quantity_used manually, they are reverting usage (so add it back to available remaining quantity)
            const addFormData = new FormData();
            addFormData.append("id", editMode.id);
            addFormData.append("quantity_to_add", Math.abs(diff));
            
            // Adjust both remaining quantity and decrement quantity_used in database
            // Let's call update-ingredient with adjusted values
            const updateFormData = new FormData();
            updateFormData.append("id", editMode.id);
            updateFormData.append("name", currentIng.name);
            updateFormData.append("unit", currentIng.unit);
            updateFormData.append("quantity", currentIng.quantity + Math.abs(diff));
            
            // We also need to update the quantity_used column. We'll update the script or handle it
            // For safety, let's call a script that handles exact inline updates. We can update update-ingredient.php to handle this.
            // Let's implement inline update directly by executing it.
            const res = await adminApiRequest({
              script: "update-ingredient",
              formData: updateFormData,
            });
            if (!res?.success) throw new Error(res?.error || "Failed to revert usage");

            // Decrement quantity_used inside database directly
            const resetUsedFormData = new FormData();
            resetUsedFormData.append("id", editMode.id);
            resetUsedFormData.append("new_used", newQtyUsed);
            await adminApiRequest({
              script: "update-ingredient-used",
              formData: resetUsedFormData,
            });
          }

          setSuccessMsg("Ingredient usage updated!");
          setTimeout(() => setSuccessMsg(""), 3000);
          setEditMode(null);
          fetchIngredients();
        }
      } catch (err) {
        setError(err.message);
        setTimeout(() => setError(null), 3000);
      } finally {
        setSubmitting(false);
      }
      return;
    }

    const currentItem = items.find((i) => i.id === editMode.id);
    if (currentItem) {
      const newRemaining =
        currentItem.inventory_available -
        currentItem.sold_quantity +
          parseInt(editForm.inventory_expired || 0, 10) +
          parseInt(editForm.inventory_house || 0, 10) +
          parseInt(editForm.inventory_broken || 0, 10);

      if (newRemaining < 0) {
        setError(
          `Cannot save: Remaining stock cannot be negative (calculated: ${newRemaining}).`,
        );
        setTimeout(() => setError(null), 5000);
        return;
      }
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("id", editMode.id);
      formData.append(
        "inventory_available",
        parseInt(editForm.inventory_available || 0, 10),
      );
      formData.append(
        "inventory_expired",
        parseInt(editForm.inventory_expired || 0, 10),
      );
      formData.append(
        "inventory_house",
        parseInt(editForm.inventory_house || 0, 10),
      );
      formData.append(
        "inventory_broken",
        parseInt(editForm.inventory_broken || 0, 10),
      );

      const res = await adminApiRequest({
        script: "update-inventory",
        formData,
      });

      if (res?.success) {
        setSuccessMsg("Inventory updated!");
        setTimeout(() => setSuccessMsg(""), 3000);
        setEditMode(null);
        fetchInventory();
      } else {
        throw new Error(res?.error || "Failed to update inventory");
      }
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(null), 3000);
    } finally {
      setSubmitting(false);
    }
  };

  const openResetModal = (item) => {
    if (item.remaining_quantity > 0) {
      setError(
        "You must exhaust a stock entirely before you can reset the values.",
      );
      setTimeout(() => setError(null), 3000);
      return;
    }
    setResetModalItem(item);
    setResetNewAvailable("0");
  };

  const confirmReset = async () => {
    const parsedVal = parseInt(resetNewAvailable, 10);
    if (isNaN(parsedVal) || parsedVal < 0) {
      setError("Invalid quantity. Please enter a valid number.");
      setTimeout(() => setError(null), 3000);
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("item_id", resetModalItem.id);
      formData.append("new_available", parsedVal);

      const res = await adminApiRequest({
        script: "reset-inventory",
        formData,
      });

      if (res?.success) {
        setSuccessMsg(`${resetModalItem.item_name} reset successfully!`);
        setTimeout(() => setSuccessMsg(""), 3000);
        fetchInventory();
        setResetModalItem(null);
      } else {
        throw new Error(res?.error || "Failed to reset inventory");
      }
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const openAddHouseModal = (item) => {
    setAddHouseModalItem(item);
    setAddHouseQuantity("");
    setAddHouseApprovedBy("");
    setAddHouseNotes("");
  };

  const confirmAddHouse = async () => {
    const qty = parseInt(addHouseQuantity, 10);
    if (!qty || qty <= 0 || !addHouseApprovedBy.trim()) {
      setError("Please enter a valid quantity and approver name.");
      setTimeout(() => setError(null), 3000);
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("item_id", addHouseModalItem.id);
      formData.append("quantity", qty);
      formData.append("approved_by", addHouseApprovedBy);
      formData.append("notes", addHouseNotes);

      const res = await adminApiRequest({
        script: "add-house-stock",
        formData,
      });

      if (res?.success) {
        setSuccessMsg(`Logged ${qty} On the House for ${addHouseModalItem.item_name}.`);
        setTimeout(() => setSuccessMsg(""), 3000);
        setAddHouseModalItem(null);
        fetchInventory();
      } else {
        throw new Error(res?.error || "Failed to add to On the House.");
      }
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const openAddStockModal = (item) => {
    setAddStockModalItem(item);
    setAddStockQuantity("");
    setAddStockExpiry("");
    setAddStockHasExpiry(false);
  };

  const confirmAddStock = async () => {
    const parsedVal = parseInt(addStockQuantity, 10);
    if (isNaN(parsedVal) || parsedVal === 0) {
      setError("Invalid quantity. Please enter a valid non-zero number.");
      setTimeout(() => setError(null), 3000);
      return;
    }

    if (addStockModalItem.remaining_quantity + parsedVal < 0) {
      setError("Cannot reduce stock below the amount already sold or expired.");
      setTimeout(() => setError(null), 3000);
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("item_id", addStockModalItem.id);
      formData.append("quantity_to_add", parsedVal);
      if (addStockHasExpiry && addStockExpiry) {
        formData.append("expiry_date", addStockExpiry);
      }

      const res = await adminApiRequest({
        script: "add-inventory-stock",
        formData,
      });

      if (res?.success) {
        setSuccessMsg(
          `Updated stock for ${addStockModalItem.item_name} by ${parsedVal} successfully!`,
        );
        setTimeout(() => setSuccessMsg(""), 3000);
        fetchInventory();
        setAddStockModalItem(null);
      } else {
        throw new Error(res?.error || "Failed to add stock");
      }
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const toggleBulkEdit = () => {
    setShowBulkEdit(!showBulkEdit);
    setSelectedItems([]);
  };

  const handleSelect = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const confirmBulkAdd = async () => {
    const parsedVal = parseInt(bulkQuantity, 10);
    if (isNaN(parsedVal) || parsedVal === 0) {
      setError("Invalid quantity. Please enter a non-zero number.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      const res = await adminApiRequest({
        script: "bulk-add-inventory-stock",
        data: {
          item_ids: JSON.stringify(selectedItems),
          quantity_to_add: parsedVal,
          expiry_date: (bulkAddHasExpiry && bulkExpiry) ? bulkExpiry : "",
        },
      });
      if (res?.success) {
        setSuccessMsg(
          `Stock added to ${selectedItems.length} items successfully!`,
        );
        setTimeout(() => setSuccessMsg(""), 3000);
        fetchInventory();
        setShowBulkAddModal(false);
        setSelectedItems([]);
        setShowBulkEdit(false);
      } else {
        throw new Error(res?.error || "Failed to add stock");
      }
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const confirmBulkReset = async () => {
    const parsedVal = parseInt(bulkQuantity, 10);
    if (isNaN(parsedVal) || parsedVal < 0) {
      setError("Invalid quantity. Please enter a valid number.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      const res = await adminApiRequest({
        script: "bulk-reset-inventory",
        data: {
          item_ids: JSON.stringify(selectedItems),
          new_available: parsedVal,
        },
      });
      if (res?.success) {
        setSuccessMsg(`Reset ${selectedItems.length} items successfully!`);
        setTimeout(() => setSuccessMsg(""), 3000);
        fetchInventory();
        setShowBulkResetModal(false);
        setSelectedItems([]);
        setShowBulkEdit(false);
      } else {
        throw new Error(res?.error || "Failed to reset inventory");
      }
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // ── Handlers: ingredients ─────────────────────────
  const handleAddIngredient = async () => {
    if (!ingredientForm.name.trim() || !ingredientForm.unit.trim()) {
      setError("Name and unit are required.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setSubmittingIngredient(true);
    try {
      const formData = new FormData();
      formData.append("name", ingredientForm.name.trim());
      formData.append("quantity", parseFloat(ingredientForm.quantity) || 0);
      formData.append("unit", ingredientForm.unit.trim());
      const res = await adminApiRequest({ script: "add-ingredient", formData });
      if (res?.success) {
        setSuccessMsg("Ingredient added successfully!");
        setTimeout(() => setSuccessMsg(""), 3000);
        setShowAddIngredientModal(false);
        setIngredientForm({ name: "", quantity: "", unit: "" });
        fetchIngredients();
      } else {
        throw new Error(res?.error || "Failed to add ingredient");
      }
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(null), 3000);
    } finally {
      setSubmittingIngredient(false);
    }
  };

  const handleAddIngredientStock = async () => {
    const qty = parseFloat(addIngredientStockQuantity);
    if (isNaN(qty) || qty === 0) {
      setError("Please enter a valid non-zero quantity.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (addIngredientStockItem.quantity + qty < 0) {
      setError("Cannot reduce stock below the quantity already used.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setSubmittingAddIngredientStock(true);
    try {
      const formData = new FormData();
      formData.append("id", addIngredientStockItem.id);
      formData.append("quantity_to_add", qty);
      const res = await adminApiRequest({ script: "add-ingredient-stock", formData });
      if (res?.success) {
        setSuccessMsg(`Stock updated for ${addIngredientStockItem.name}.`);
        setTimeout(() => setSuccessMsg(""), 3000);
        setAddIngredientStockItem(null);
        setAddIngredientStockQuantity("");
        fetchIngredients();
      } else {
        throw new Error(res?.error || "Failed to add ingredient stock");
      }
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(null), 3000);
    } finally {
      setSubmittingAddIngredientStock(false);
    }
  };

  const handleResetIngredient = async () => {
    const newQty = parseFloat(resetIngredientNewQty);
    if (isNaN(newQty) || newQty < 0) {
      setError("Please enter a valid starting quantity.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setSubmittingResetIngredient(true);
    try {
      const formData = new FormData();
      formData.append("id", resetIngredientItem.id);
      formData.append("new_quantity", newQty);
      const res = await adminApiRequest({ script: "reset-ingredient", formData });
      if (res?.success) {
        setSuccessMsg(`Ingredient ${resetIngredientItem.name} has been reset.`);
        setTimeout(() => setSuccessMsg(""), 3000);
        setResetIngredientItem(null);
        setResetIngredientNewQty("0");
        fetchIngredients();
      } else {
        throw new Error(res?.error || "Failed to reset ingredient");
      }
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(null), 3000);
    } finally {
      setSubmittingResetIngredient(false);
    }
  };

  const handleDeleteIngredient = async () => {
    setSubmittingDeleteIngredient(true);
    try {
      const formData = new FormData();
      formData.append("id", deleteIngredientItem.id);
      const res = await adminApiRequest({ script: "delete-ingredient", formData });
      if (res?.success) {
        setSuccessMsg(`Ingredient ${deleteIngredientItem.name} deleted.`);
        setTimeout(() => setSuccessMsg(""), 3000);
        setDeleteIngredientItem(null);
        fetchIngredients();
      } else {
        throw new Error(res?.error || "Failed to delete ingredient");
      }
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(null), 3000);
    } finally {
      setSubmittingDeleteIngredient(false);
    }
  };

  const toggleSection = (section) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // ── Derived data ──────────────────────────────────
  const filteredItems = items.filter((item) => {
    const matchesSearch = item.item_name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesCategory =
      categoryFilter === "all" ||
      String(item.category_id) === String(categoryFilter);
    return matchesSearch && matchesCategory;
  });

  const sortedFilteredItems = [...filteredItems].sort((a, b) => {
    const priority = { 1: 1, 17: 2, 16: 99 };
    const aPriority = priority[a.category_id] || 50;
    const bPriority = priority[b.category_id] || 50;

    if (aPriority !== bPriority) return aPriority - bPriority;

    const catA = a.category_name || "";
    const catB = b.category_name || "";
    if (catA !== catB) return catA.localeCompare(catB);

    const nameA = a.item_name || "";
    const nameB = b.item_name || "";
    return nameA.localeCompare(nameB);
  });

  const filteredHistoryItems = historyItems.filter((item) => {
    const matchesSearch = item.item_name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesCategory =
      categoryFilter === "all" ||
      String(item.category_id) === String(categoryFilter);
    return matchesSearch && matchesCategory;
  });

  const sortedFilteredHistoryItems = [...filteredHistoryItems].sort((a, b) => {
    const priority = { 1: 1, 17: 2, 16: 99 };
    const aPriority = priority[a.category_id] || 50;
    const bPriority = priority[b.category_id] || 50;

    if (aPriority !== bPriority) return aPriority - bPriority;

    const catA = a.category_name || "";
    const catB = b.category_name || "";
    if (catA !== catB) return catA.localeCompare(catB);

    const nameA = a.item_name || "";
    const nameB = b.item_name || "";
    return nameA.localeCompare(nameB);
  });

  // Drink category IDs: 16 (Drinks parent) and all its children (8, 9, 11, 12)
  const drinkCategoryIds = useMemo(() => {
    const ids = new Set([8, 9, 11, 12, 16]); // hardcoded known IDs
    categories.forEach((cat) => {
      if (Number(cat.id) === 16) {
        ids.add(16);
        (cat.subcategories || []).forEach((sub) => ids.add(Number(sub.id)));
      }
    });
    return ids;
  }, [categories]);

  const foodItems = sortedFilteredItems.filter(
    (item) => !drinkCategoryIds.has(Number(item.category_id)),
  );
  const drinkItems = sortedFilteredItems.filter((item) =>
    drinkCategoryIds.has(Number(item.category_id)),
  );
  const filteredIngredients = ingredients.filter((ing) =>
    ing.name.toLowerCase().includes(ingredientSearchTerm.toLowerCase()),
  );

  const historyFoodItems = sortedFilteredHistoryItems.filter(
    (item) => !drinkCategoryIds.has(Number(item.category_id)),
  );
  const historyDrinkItems = sortedFilteredHistoryItems.filter((item) =>
    drinkCategoryIds.has(Number(item.category_id)),
  );
  const filteredIngredientsHistory = ingredientsHistory.filter((ing) =>
    ing.ingredient_name.toLowerCase().includes(ingredientSearchTerm.toLowerCase()),
  );

  const totalItems = items.length;
  const runningLowCount = items.filter(
    (item) => item.remaining_quantity > 0 && item.remaining_quantity < 5,
  ).length;
  const outOfStockCount = items.filter(
    (item) => item.remaining_quantity <= 0,
  ).length;

  const threeMonthsFromNow = new Date();
  threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);

  const expiringSoonCount = items.filter(
    (item) =>
      item.remaining_quantity > 0 &&
      item.expiry_date &&
      new Date(item.expiry_date) <= threeMonthsFromNow,
  ).length;

  // ── Shared inventory table renderer (Food & Drinks) ──
  const renderInventoryTable = (tableItems) => {
    const allSectionIds = tableItems.map((i) => i.id);
    const allSelected =
      allSectionIds.length > 0 &&
      allSectionIds.every((id) => selectedItems.includes(id));

    return (
      <div className="w-full overflow-x-auto overflow-y-auto overscroll-none">
        <table className="w-full md:min-w-[800px] text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              {showBulkEdit && (
                <th className="px-6 py-4 w-12">
                  <input
                    type="checkbox"
                    className="cursor-pointer rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                    checked={allSelected}
                    onChange={(e) => {
                      if (e.target.checked)
                        setSelectedItems((prev) => [
                          ...new Set([...prev, ...allSectionIds]),
                        ]);
                      else
                        setSelectedItems((prev) =>
                          prev.filter((id) => !allSectionIds.includes(id)),
                        );
                    }}
                  />
                </th>
              )}
              <th className="px-6 py-4 font-semibold">Item Name</th>
              <th className="px-6 py-4 font-semibold text-slate-500">
                Total Stock
              </th>
              <th className="px-6 py-4 font-semibold text-emerald-600">
                Sold (Orders)
              </th>
              <th className="px-6 py-4 font-semibold text-red-500">Expired</th>
              <th className="px-6 py-4 font-semibold text-orange-500">
                On the House
              </th>
              <th className="px-6 py-4 font-semibold text-amber-600">Broken</th>
              <th className="px-6 py-4 font-semibold text-blue-600">
                Remaining
              </th>
              <th className="px-6 py-4 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {loading ? (
              <tr>
                <td
                  colSpan={showBulkEdit ? "9" : "8"}
                  className="px-6 py-8 text-center text-slate-500"
                >
                  Loading inventory...
                </td>
              </tr>
            ) : tableItems.length === 0 ? (
              <tr>
                <td
                  colSpan={showBulkEdit ? "9" : "8"}
                  className="px-6 py-8 text-center text-slate-500"
                >
                  No items found.
                </td>
              </tr>
            ) : (
              tableItems.map((item) => {
                const isOut = item.remaining_quantity <= 0;
                const isLow =
                  item.remaining_quantity > 0 && item.remaining_quantity < 5;
                const rowClass = isOut
                  ? "bg-red-50 hover:bg-red-100/70"
                  : isLow
                    ? "bg-orange-50/50 hover:bg-orange-100/50"
                    : "hover:bg-slate-50 transition-colors";

                return (
                  <tr key={item.id} className={rowClass}>
                    {showBulkEdit && (
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          className="cursor-pointer rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                          checked={selectedItems.includes(item.id)}
                          onChange={() => handleSelect(item.id)}
                        />
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <div className="font-medium text-slate-900 text-sm sm:text-base">
                        {item.item_name}
                      </div>
                      {item.category_name && (
                        <div className="text-xs sm:text-sm text-slate-500 mt-1">
                          {item.category_name}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 font-bold text-slate-700 text-base sm:text-lg">
                      {item.inventory_available}
                    </td>
                    <td className="px-6 py-4 font-bold text-emerald-600 text-base sm:text-lg">
                      {item.sold_quantity}
                    </td>

                    <td className="px-6 py-4 font-bold text-red-500 group text-base sm:text-lg">
                      {editMode?.id === item.id &&
                      editMode?.field === "expired" ? (
                        <input
                          type="number"
                          name="inventory_expired"
                          value={editForm.inventory_expired}
                          onChange={handleFormChange}
                          onBlur={() => setTimeout(handleCancelEdit, 200)}
                          autoFocus
                          className="w-20 rounded border border-slate-300 px-2 py-1 text-base focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
                          min="0"
                        />
                      ) : (
                        <div className="flex items-center gap-1">
                          <span>
                            {editMode?.id === item.id
                              ? editForm.inventory_expired
                              : item.inventory_expired}
                          </span>
                          <button
                            onClick={() => handleEditClick(item, "expired")}
                            className={`p-1 hover:bg-red-100 rounded transition-opacity text-red-400 hover:text-red-600 ${editMode?.id === item.id ? "hidden" : "opacity-100 sm:opacity-0 sm:group-hover:opacity-100"}`}
                            title="Edit"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="w-4 h-4"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.89 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.89l12.67-12.67Z"
                              />
                            </svg>
                          </button>
                        </div>
                      )}
                    </td>

                    <td className="px-6 py-4 font-bold text-orange-500 group text-base sm:text-lg">
                      <div className="flex items-center gap-4">
                        <span
                          className="cursor-pointer rounded px-2 py-1 border border-orange-200 hover:bg-orange-50 transition-colors text-center min-w-[2.5rem]"
                          onClick={() => {
                            setViewHouseLogsItem(item);
                            setLoadingHouseLogs(true);
                            setHouseLogs([]);
                            adminApiRequest({
                              script: "fetch-house-logs",
                              formData: (() => {
                                const f = new FormData();
                                f.append("item_id", item.id);
                                return f;
                              })(),
                            }).then((res) => {
                              if (res?.success) setHouseLogs(res.data || []);
                              setLoadingHouseLogs(false);
                            });
                          }}
                          title="View History"
                        >
                          {item.inventory_house || 0}
                        </span>
                        <button
                          onClick={() => openAddHouseModal(item)}
                          className={`p-2 -ml-2 hover:bg-orange-100 rounded transition-opacity text-orange-400 hover:text-orange-600 opacity-100 sm:opacity-0 sm:group-hover:opacity-100`}
                          title="Log On the House"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-4 h-4"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M12 4.5v15m7.5-7.5h-15"
                            />
                          </svg>
                        </button>
                      </div>
                    </td>

                    <td className="px-6 py-4 font-bold text-amber-600 group text-base sm:text-lg">
                      {editMode?.id === item.id &&
                      editMode?.field === "broken" ? (
                        <input
                          type="number"
                          name="inventory_broken"
                          value={editForm.inventory_broken}
                          onChange={handleFormChange}
                          onBlur={() => setTimeout(handleCancelEdit, 200)}
                          autoFocus
                          className="w-20 rounded border border-slate-300 px-2 py-1 text-base focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                          min="0"
                        />
                      ) : (
                        <div className="flex items-center gap-1">
                          <span>
                            {editMode?.id === item.id
                              ? editForm.inventory_broken
                              : item.inventory_broken || 0}
                          </span>
                          <button
                            onClick={() => handleEditClick(item, "broken")}
                            className={`p-1 hover:bg-amber-100 rounded transition-opacity text-amber-400 hover:text-amber-600 ${editMode?.id === item.id ? "hidden" : "opacity-100 sm:opacity-0 sm:group-hover:opacity-100"}`}
                            title="Edit"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="w-4 h-4"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.89 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.89l12.67-12.67Z"
                              />
                            </svg>
                          </button>
                        </div>
                      )}
                    </td>

                    <td
                      className={`px-6 py-4 font-black text-xl sm:text-2xl ${
                        item.remaining_quantity <= 0
                          ? "text-red-600"
                          : item.remaining_quantity < 5
                            ? "text-orange-500"
                            : "text-blue-600"
                      }`}
                    >
                      {editMode?.id === item.id
                        ? editForm.inventory_available -
                          (item.sold_quantity +
                            parseInt(editForm.inventory_expired || 0, 10) +
                            parseInt(editForm.inventory_house || 0, 10) +
                            parseInt(editForm.inventory_broken || 0, 10))
                        : item.remaining_quantity}
                    </td>

                    <td className="px-6 py-4 text-right">
                      {editMode?.id === item.id ? (
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={handleSave}
                            disabled={submitting}
                            className="rounded bg-emerald-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            disabled={submitting}
                            className="rounded bg-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-300 disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => openAddStockModal(item)}
                            className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 transition-colors"
                          >
                            + Add
                          </button>
                          <button
                            onClick={() => openResetModal(item)}
                            className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 hover:border-red-300 transition-colors"
                          >
                            Reset
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    );
  };

  // ── JSX ──────────────────────────────────────────
  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="mb-6 flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">
            Inventory Management
          </h2>
          <p className="text-sm text-slate-500">
            Track available, sold, expired, house, and broken items.
          </p>
        </div>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex w-full flex-row gap-2 sm:flex-1 sm:items-center sm:gap-4">
            <div className="relative flex-1 sm:w-full sm:max-w-md">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                placeholder="Search inventory..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-xl border border-slate-200 py-2 sm:py-2.5 pl-10 pr-4 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 transition-colors"
              />
            </div>

            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="flex-1 sm:w-auto rounded-xl border border-slate-200 bg-white py-2 sm:py-2.5 pl-2 pr-8 sm:pl-4 sm:pr-10 text-xs sm:text-sm text-slate-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="all">All Categories</option>
              {[...categories]
                .sort((a, b) => {
                  const priority = { 1: 1, 17: 2, 16: 99 };
                  const aPriority = priority[a.id] || 50;
                  const bPriority = priority[b.id] || 50;
                  return aPriority - bPriority;
                })
                .map((cat) => (
                  <React.Fragment key={cat.id}>
                    <option value={cat.id}>{cat.category_name}</option>
                    {(cat.subcategories || []).map((subcat) => (
                      <option key={subcat.id} value={subcat.id}>
                        - {subcat.category_name}
                      </option>
                    ))}
                  </React.Fragment>
                ))}
            </select>
          </div>
          <div className="grid w-full grid-cols-3 gap-1 sm:gap-2 sm:flex sm:w-auto sm:gap-4">
            <button
              onClick={toggleBulkEdit}
              className={`flex w-full items-center justify-center gap-1 sm:gap-2 rounded-xl px-2 py-2 sm:px-5 sm:py-2.5 text-xs sm:text-sm font-semibold shadow-sm transition-all hover:shadow-md whitespace-nowrap ${
                showBulkEdit
                  ? "bg-rose-600 text-white hover:bg-rose-700"
                  : "bg-slate-900 text-white hover:bg-slate-800"
              }`}
            >
              {showBulkEdit ? "Close Bulk Edit" : "Bulk Edit"}
            </button>
            <button
              onClick={() =>
                setViewMode(viewMode === "live" ? "history" : "live")
              }
              className={`flex w-full items-center justify-center gap-1 sm:gap-2 rounded-xl px-2 py-2 sm:px-5 sm:py-2.5 text-xs sm:text-sm font-semibold shadow-sm transition-all hover:shadow-md whitespace-nowrap ${
                viewMode === "history"
                  ? "bg-blue-600 text-white hover:bg-blue-700"
                  : "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              {viewMode === "history" ? "Live Inventory" : "History View"}
            </button>
            <button
              onClick={() => {
                fetchInventory();
                fetchCategories();
                fetchIngredients();
              }}
              className="flex w-full items-center justify-center gap-1 sm:gap-2 rounded-xl bg-indigo-600 px-2 py-2 sm:px-5 sm:py-2.5 text-xs sm:text-sm font-semibold text-white shadow-sm transition-all hover:bg-indigo-700 hover:shadow-md whitespace-nowrap"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="h-4 w-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                />
              </svg>
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* History date range picker */}
      {viewMode === "history" && (
        <div className="mb-6 rounded-2xl border border-indigo-200 bg-indigo-50 p-4 shadow-sm flex flex-col md:flex-row md:items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              From Date
            </label>
            <input
              type="date"
              value={historyStartDate}
              onChange={(e) => setHistoryStartDate(e.target.value)}
              className="rounded-xl border border-slate-200 px-4 py-2 text-slate-900 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              To Date
            </label>
            <input
              type="date"
              value={historyEndDate}
              onChange={(e) => setHistoryEndDate(e.target.value)}
              className="rounded-xl border border-slate-200 px-4 py-2 text-slate-900 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            />
          </div>
        </div>
      )}

      {/* Bulk Edit bar */}
      {showBulkEdit && viewMode === "live" && (
        <div className="mb-6 rounded-2xl border border-indigo-200 bg-indigo-50 p-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm text-indigo-900">
            <span className="font-bold">{selectedItems.length}</span> items
            selected
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => {
                if (selectedItems.length === 0) {
                  setError("Select items first");
                  setTimeout(() => setError(null), 3000);
                  return;
                }
                setBulkQuantity("");
                setBulkExpiry("");
                setBulkAddHasExpiry(false);
                setShowBulkAddModal(true);
              }}
              className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
            >
              + Bulk Add
            </button>
            <button
              onClick={() => {
                if (selectedItems.length === 0) {
                  setError("Select items first");
                  setTimeout(() => setError(null), 3000);
                  return;
                }
                const invalidItems = selectedItems.filter((id) => {
                  const item = items.find((i) => i.id === id);
                  return item && item.remaining_quantity > 0;
                });
                if (invalidItems.length > 0) {
                  setError(
                    "You cannot bulk reset items that are not fully exhausted (remaining quantity must be 0).",
                  );
                  setTimeout(() => setError(null), 3000);
                  return;
                }
                setBulkQuantity("");
                setShowBulkResetModal(true);
              }}
              className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
            >
              Bulk Reset
            </button>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {viewMode === "live" && (
        <div className="mb-4 sm:mb-6 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
          <button
            onClick={() => setInfoModalStatus("all")}
            className="flex flex-col items-center sm:items-start justify-center rounded-2xl border border-blue-200 bg-blue-50 p-2 sm:p-4 text-center sm:text-left transition-all hover:bg-blue-100 hover:border-blue-300 hover:shadow-md cursor-pointer"
          >
            <span className="text-xs sm:text-sm font-medium text-blue-600 leading-tight">
              Total Items
            </span>
            <span className="mt-1 sm:mt-2 text-lg sm:text-2xl font-bold text-blue-900">
              {totalItems}
            </span>
          </button>

          <button
            onClick={() => setInfoModalStatus("low")}
            className="flex flex-col items-center sm:items-start justify-center rounded-2xl border border-orange-200 bg-orange-50 p-2 sm:p-4 text-center sm:text-left transition-all hover:bg-orange-100 hover:border-orange-300 hover:shadow-md cursor-pointer"
          >
            <span className="text-xs sm:text-sm font-medium text-orange-600 leading-tight">
              <span className="sm:hidden">Low {"(<5)"}</span>
              <span className="hidden sm:inline">Running Low {"(< 5)"}</span>
            </span>
            <span className="mt-1 sm:mt-2 text-lg sm:text-2xl font-bold text-orange-900">
              {runningLowCount}
            </span>
          </button>

          <button
            onClick={() => setInfoModalStatus("out")}
            className="flex flex-col items-center sm:items-start justify-center rounded-2xl border border-red-200 bg-red-50 p-2 sm:p-4 text-center sm:text-left transition-all hover:bg-red-100 hover:border-red-300 hover:shadow-md cursor-pointer"
          >
            <span className="text-xs sm:text-sm font-medium text-red-600 leading-tight">
              <span className="sm:hidden">Finished {"(<=0)"}</span>
              <span className="hidden sm:inline">Out of Stock {"(<= 0)"}</span>
            </span>
            <span className="mt-1 sm:mt-2 text-lg sm:text-2xl font-bold text-red-900">
              {outOfStockCount}
            </span>
          </button>

          <button
            onClick={() => setInfoModalStatus("expiring")}
            className="flex flex-col items-center sm:items-start justify-center rounded-2xl border border-purple-200 bg-purple-50 p-2 sm:p-4 text-center sm:text-left transition-all hover:bg-purple-100 hover:border-purple-300 hover:shadow-md cursor-pointer"
          >
            <span className="text-xs sm:text-sm font-medium text-purple-600 leading-tight">
              <span className="sm:hidden">Expiring {"(<=3 months)"}</span>
              <span className="hidden sm:inline">
                Expiring Soon {"(<= 3 months)"}
              </span>
            </span>
            <span className="mt-1 sm:mt-2 text-lg sm:text-2xl font-bold text-purple-900">
              {expiringSoonCount}
            </span>
          </button>
        </div>
      )}

      {successMsg && (
        <div className="mb-4 rounded-lg bg-emerald-50 p-4 text-sm text-emerald-600">
          {successMsg}
        </div>
      )}

      {/* ── HISTORY VIEW ─────────────────────────────── */}
      {viewMode === "history" ? (
        <div className="flex-1 min-h-0 space-y-4 overflow-y-auto pb-4">
          
          {/* FOOD HISTORY SECTION */}
          <AccordionSection
            title="🍽️ Food History"
            count={historyFoodItems.length}
            isOpen={openSections.historyFood}
            onToggle={() => setOpenSections(prev => ({ ...prev, historyFood: !prev.historyFood }))}
            badgeClass="bg-emerald-100 text-emerald-700"
          >
            <div className="w-full overflow-x-auto overflow-y-auto overscroll-none">
              <table className="w-full md:min-w-[800px] text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-6 py-4 font-semibold">Item Name</th>
                    <th className="px-6 py-4 font-semibold text-slate-500">Total Available</th>
                    <th className="px-6 py-4 font-semibold text-emerald-600">Total Sold</th>
                    <th className="px-6 py-4 font-semibold text-red-500">Total Expired</th>
                    <th className="px-6 py-4 font-semibold text-orange-500">Total On the House</th>
                    <th className="px-6 py-4 font-semibold text-amber-600">Total Broken</th>
                    <th className="px-6 py-4 font-semibold text-blue-600">Remaining</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {loadingHistory ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center text-slate-500">Loading food history...</td>
                    </tr>
                  ) : historyFoodItems.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center text-slate-500">No food history found for this period.</td>
                    </tr>
                  ) : (
                    historyFoodItems.map((item) => (
                      <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="font-medium text-slate-900 text-sm sm:text-base">{item.item_name}</div>
                          {item.category_name && <div className="text-xs sm:text-sm text-slate-500 mt-1">{item.category_name}</div>}
                        </td>
                        <td className="px-6 py-4 font-bold text-slate-700 text-base sm:text-lg">{item.total_available}</td>
                        <td className="px-6 py-4 font-bold text-emerald-600 text-base sm:text-lg">{item.total_sold}</td>
                        <td className="px-6 py-4 font-bold text-red-500 text-base sm:text-lg">{item.total_expired}</td>
                        <td className="px-6 py-4 font-bold text-orange-500 text-base sm:text-lg">{item.total_house}</td>
                        <td className="px-6 py-4 font-bold text-amber-600 text-base sm:text-lg">{item.total_broken || 0}</td>
                        <td className="px-6 py-4 font-black text-blue-600 text-lg sm:text-xl">{item.remaining_quantity}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </AccordionSection>

          {/* DRINKS HISTORY SECTION */}
          <AccordionSection
            title="🍹 Drinks History"
            count={historyDrinkItems.length}
            isOpen={openSections.historyDrinks}
            onToggle={() => setOpenSections(prev => ({ ...prev, historyDrinks: !prev.historyDrinks }))}
            badgeClass="bg-blue-100 text-blue-700"
          >
            <div className="w-full overflow-x-auto overflow-y-auto overscroll-none">
              <table className="w-full md:min-w-[800px] text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-6 py-4 font-semibold">Item Name</th>
                    <th className="px-6 py-4 font-semibold text-slate-500">Total Available</th>
                    <th className="px-6 py-4 font-semibold text-emerald-600">Total Sold</th>
                    <th className="px-6 py-4 font-semibold text-red-500">Total Expired</th>
                    <th className="px-6 py-4 font-semibold text-orange-500">Total On the House</th>
                    <th className="px-6 py-4 font-semibold text-amber-600">Total Broken</th>
                    <th className="px-6 py-4 font-semibold text-blue-600">Remaining</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {loadingHistory ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center text-slate-500">Loading drinks history...</td>
                    </tr>
                  ) : historyDrinkItems.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center text-slate-500">No drinks history found for this period.</td>
                    </tr>
                  ) : (
                    historyDrinkItems.map((item) => (
                      <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="font-medium text-slate-900 text-sm sm:text-base">{item.item_name}</div>
                          {item.category_name && <div className="text-xs sm:text-sm text-slate-500 mt-1">{item.category_name}</div>}
                        </td>
                        <td className="px-6 py-4 font-bold text-slate-700 text-base sm:text-lg">{item.total_available}</td>
                        <td className="px-6 py-4 font-bold text-emerald-600 text-base sm:text-lg">{item.total_sold}</td>
                        <td className="px-6 py-4 font-bold text-red-500 text-base sm:text-lg">{item.total_expired}</td>
                        <td className="px-6 py-4 font-bold text-orange-500 text-base sm:text-lg">{item.total_house}</td>
                        <td className="px-6 py-4 font-bold text-amber-600 text-base sm:text-lg">{item.total_broken || 0}</td>
                        <td className="px-6 py-4 font-black text-blue-600 text-lg sm:text-xl">{item.remaining_quantity}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </AccordionSection>

          {/* INGREDIENTS HISTORY SECTION */}
          <AccordionSection
            title="🌿 Ingredients History"
            count={filteredIngredientsHistory.length}
            isOpen={openSections.historyIngredients}
            onToggle={() => setOpenSections(prev => ({ ...prev, historyIngredients: !prev.historyIngredients }))}
            badgeClass="bg-violet-100 text-violet-700"
          >
            <div className="w-full overflow-x-auto overflow-y-auto overscroll-none">
              <table className="w-full md:min-w-[800px] text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-6 py-3 font-semibold">Name</th>
                    <th className="px-6 py-3 font-semibold text-slate-500">Total Available</th>
                    <th className="px-6 py-3 font-semibold text-orange-600">Total Used</th>
                    <th className="px-6 py-3 font-semibold text-violet-600">Remaining</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {loadingIngredientsHistory ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-8 text-center text-slate-500">Loading ingredients history...</td>
                    </tr>
                  ) : filteredIngredientsHistory.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-8 text-center text-slate-500">No ingredients history found for this period.</td>
                    </tr>
                  ) : (
                    filteredIngredientsHistory.map((ing) => (
                      <tr key={ing.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="font-medium text-slate-900 text-sm sm:text-base">{ing.ingredient_name}</div>
                        </td>
                        <td className="px-6 py-4 font-bold text-slate-700 text-base sm:text-lg">
                          {ing.total_stock} <span className="text-xs font-normal text-slate-400">{ing.unit}</span>
                        </td>
                        <td className="px-6 py-4 font-bold text-orange-500 text-base sm:text-lg">
                          {ing.quantity_used} <span className="text-xs font-normal text-slate-400">{ing.unit}</span>
                        </td>
                        <td className="px-6 py-4 font-black text-violet-600 text-xl sm:text-2xl">
                          {ing.remaining_quantity} <span className="text-xs font-normal text-slate-400">{ing.unit}</span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </AccordionSection>

        </div>
      ) : (
        /* ── LIVE INVENTORY: 3 Accordion Sections ───── */
        <div className="flex-1 min-h-0 space-y-4 overflow-y-auto pb-4">

          {/* FOOD SECTION */}
          <AccordionSection
            title="🍽️ Food"
            count={foodItems.length}
            isOpen={openSections.food}
            onToggle={() => toggleSection("food")}
            badgeClass="bg-emerald-100 text-emerald-700"
          >
            {renderInventoryTable(foodItems)}
          </AccordionSection>

          {/* DRINKS SECTION */}
          <AccordionSection
            title="🍹 Drinks"
            count={drinkItems.length}
            isOpen={openSections.drinks}
            onToggle={() => toggleSection("drinks")}
            badgeClass="bg-blue-100 text-blue-700"
          >
            {renderInventoryTable(drinkItems)}
          </AccordionSection>

          {/* INGREDIENTS SECTION */}
          <AccordionSection
            title="🌿 Ingredients"
            count={filteredIngredients.length}
            isOpen={openSections.ingredients}
            onToggle={() => toggleSection("ingredients")}
            badgeClass="bg-violet-100 text-violet-700"
          >
            {/* Ingredient search bar & Add Item button */}
            <div className="flex flex-row items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-slate-100 bg-slate-50/50">
              <div className="relative w-full">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  placeholder="Search ingredients..."
                  value={ingredientSearchTerm}
                  onChange={(e) => setIngredientSearchTerm(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 py-1.5 pl-9 pr-4 text-sm text-slate-900 focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500 bg-white"
                />
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAddIngredientModal(true);
                }}
                className="flex shrink-0 items-center justify-center gap-1 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700 transition-colors shadow-sm"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2.5}
                  stroke="currentColor"
                  className="w-3.5 h-3.5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 4.5v15m7.5-7.5h-15"
                  />
                </svg>
                Add Item
              </button>
            </div>

            <IngredientsTable
              ingredients={filteredIngredients}
              loadingIngredients={loadingIngredients}
              openEditIngredient={(ing) => {
                setEditMode({ id: ing.id, field: "name" });
                setEditForm({ name: ing.name, quantity_used: ing.quantity_used });
              }}
              setAddIngredientStockItem={setAddIngredientStockItem}
              setAddIngredientStockQuantity={setAddIngredientStockQuantity}
              setResetIngredientItem={setResetIngredientItem}
              setResetIngredientNewQty={setResetIngredientNewQty}
              setDeleteIngredientItem={setDeleteIngredientItem}
              setError={setError}
              editMode={editMode}
              editForm={editForm}
              handleFormChange={handleFormChange}
              handleEditClick={handleEditClick}
              handleCancelEdit={handleCancelEdit}
              handleSave={handleSave}
            />
          </AccordionSection>
        </div>
      )}

      {/* ══ MODALS ══════════════════════════════════════ */}

      {/* Reset Modal */}
      {resetModalItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="mb-2 text-lg font-bold text-slate-900">
              Reset Inventory
            </h3>
            <p className="mb-6 text-sm text-slate-500">
              You are about to archive and reset{" "}
              <strong className="text-slate-800">
                {resetModalItem.item_name}
              </strong>
              . Please enter the new{" "}
              <span className="font-medium text-slate-700">
                Total Available
              </span>{" "}
              quantity to start the new cycle.
            </p>
            <div className="mb-8">
              <label className="mb-2 block text-sm font-medium text-slate-700">
                New Total Available
              </label>
              <input
                type="number"
                min="0"
                value={resetNewAvailable}
                onChange={(e) => setResetNewAvailable(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-900 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500 transition-colors"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setResetModalItem(null)}
                className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmReset}
                disabled={loading}
                className="rounded-xl bg-red-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {loading ? "Resetting..." : "Confirm Reset"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add House Modal */}
      {addHouseModalItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="mb-2 text-lg font-bold text-slate-900">
              Log On the House
            </h3>
            <p className="mb-6 text-sm text-slate-500">
              Log "On the house" stock for{" "}
              <strong className="text-slate-800">
                {addHouseModalItem.item_name}
              </strong>
              .
            </p>
            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Quantity to Add
              </label>
              <input
                type="number"
                min="1"
                value={addHouseQuantity}
                onChange={(e) => setAddHouseQuantity(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-300 px-4 py-2 text-slate-900 focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
              />
            </div>
            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Approved By
              </label>
              <input
                type="text"
                value={addHouseApprovedBy}
                onChange={(e) => setAddHouseApprovedBy(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-300 px-4 py-2 text-slate-900 focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
              />
            </div>
            <div className="mb-8">
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Notes
              </label>
              <textarea
                value={addHouseNotes}
                onChange={(e) => setAddHouseNotes(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-300 px-4 py-2 text-slate-900 focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
                rows="2"
              />
              <div className="mt-4 p-4 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-between">
                <span className="text-sm font-semibold text-orange-800">
                  Total On the House:
                </span>
                <div className="text-right">
                  <span className="text-2xl font-black text-orange-600">
                    {(addHouseModalItem.inventory_house || 0) +
                      (parseInt(addHouseQuantity, 10) || 0)}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setAddHouseModalItem(null)}
                className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmAddHouse}
                disabled={loading}
                className="rounded-xl bg-orange-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-50 transition-colors"
              >
                {loading ? "Logging..." : "Log On the House"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View House Logs Modal */}
      {viewHouseLogsItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-slate-900">
                On the House History - {viewHouseLogsItem.item_name}
              </h3>
              <button
                onClick={() => setViewHouseLogsItem(null)}
                className="p-2 sm:p-0 text-2xl sm:text-base text-slate-400 hover:text-slate-600 font-bold"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-auto rounded-xl border border-slate-200 p-2 bg-slate-50">
              {loadingHouseLogs ? (
                <div className="text-center py-8 text-slate-500">
                  Loading history...
                </div>
              ) : houseLogs.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No previous "On the House" records found.
                </div>
              ) : (
                <ul className="divide-y divide-slate-200">
                  {houseLogs.map((log) => (
                    <li
                      key={log.id}
                      className="py-3 px-2 flex justify-between items-start"
                    >
                      <div>
                        <div className="font-semibold text-slate-800">
                          Approved by:{" "}
                          <span className="text-orange-600">
                            {log.approved_by}
                          </span>
                        </div>
                        {log.notes && (
                          <div className="text-sm text-slate-600 mt-1 italic">
                            "{log.notes}"
                          </div>
                        )}
                        <div className="text-xs text-slate-500 mt-1">
                          {new Date(log.created_at).toLocaleDateString(
                            "en-GB",
                            {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            },
                          )}{" "}
                          {new Date(log.created_at).toLocaleTimeString(
                            "en-GB",
                            {
                              hour: "2-digit",
                              minute: "2-digit",
                            },
                          )}
                        </div>
                      </div>
                      <div className="font-black text-orange-500 text-lg">
                        +{log.quantity}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Stock Modal */}
      {addStockModalItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="mb-2 text-lg font-bold text-slate-900">
              Add Stock
            </h3>
            <p className="mb-6 text-sm text-slate-500">
              Add new stock for{" "}
              <strong className="text-slate-800">
                {addStockModalItem.item_name}
              </strong>
              .
            </p>
            <div className="mb-8">
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Quantity to Add/Remove (use - to reduce)
              </label>
              <input
                type="number"
                value={addStockQuantity}
                onChange={(e) => setAddStockQuantity(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-300 px-4 py-2 text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
              <div
                className="mt-4 mb-3 flex items-center gap-3 cursor-pointer select-none"
                onClick={() => setAddStockHasExpiry(!addStockHasExpiry)}
              >
                <div
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${addStockHasExpiry ? "bg-emerald-600" : "bg-slate-300"}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${addStockHasExpiry ? "translate-x-6" : "translate-x-1"}`}
                  />
                </div>
                <span className="text-sm font-medium text-slate-700">
                  Set Expiry Date
                </span>
              </div>
              {addStockHasExpiry && (
                <input
                  type="date"
                  value={addStockExpiry}
                  onChange={(e) => setAddStockExpiry(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-300 px-4 py-2 text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 transition-colors"
                />
              )}
              <div className="mt-4 p-4 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-between">
                <span className="text-sm font-semibold text-emerald-800">
                  New Total Stock:
                </span>
                <div className="text-right">
                  <span className="text-2xl font-black text-emerald-600">
                    {addStockModalItem.inventory_available +
                      (parseInt(addStockQuantity, 10) || 0)}
                  </span>
                  {addStockModalItem.remaining_quantity +
                    (parseInt(addStockQuantity, 10) || 0) <
                    0 && (
                    <div className="text-xs text-red-500 font-medium mt-1">
                      (Warning: Negative remaining stock)
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setAddStockModalItem(null)}
                className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmAddStock}
                disabled={loading}
                className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              >
                {loading ? "Adding..." : "Add Stock"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Add Modal */}
      {showBulkAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="mb-2 text-lg font-bold text-slate-900">
              Bulk Add Stock
            </h3>
            <p className="mb-6 text-sm text-slate-500">
              You are adding stock to{" "}
              <strong className="text-slate-800">
                {selectedItems.length} selected items
              </strong>
              . The quantity entered below will be added to the total available
              stock for ALL selected items.
            </p>
            <div className="mb-8">
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Quantity to Add/Remove per Item (use - to reduce)
              </label>
              <input
                type="number"
                value={bulkQuantity}
                onChange={(e) => setBulkQuantity(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 transition-colors"
              />
              <div
                className="mt-4 mb-3 flex items-center gap-3 cursor-pointer select-none"
                onClick={() => setBulkAddHasExpiry(!bulkAddHasExpiry)}
              >
                <div
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${bulkAddHasExpiry ? "bg-emerald-600" : "bg-slate-300"}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${bulkAddHasExpiry ? "translate-x-6" : "translate-x-1"}`}
                  />
                </div>
                <span className="text-sm font-medium text-slate-700">
                  Set Expiry Date
                </span>
              </div>
              {bulkAddHasExpiry && (
                <input
                  type="date"
                  value={bulkExpiry}
                  onChange={(e) => setBulkExpiry(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 transition-colors"
                />
              )}
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowBulkAddModal(false)}
                className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmBulkAdd}
                disabled={loading}
                className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              >
                {loading ? "Adding..." : "Bulk Add Stock"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Reset Modal */}
      {showBulkResetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="mb-2 text-lg font-bold text-slate-900">
              Bulk Reset Inventory
            </h3>
            <p className="mb-6 text-sm text-slate-500">
              You are about to archive and reset{" "}
              <strong className="text-slate-800">
                {selectedItems.length} selected items
              </strong>
              . All selected items will have their stock set to the new value
              entered below.
            </p>
            <div className="mb-8">
              <label className="mb-2 block text-sm font-medium text-slate-700">
                New Total Available (Per Item)
              </label>
              <input
                type="number"
                min="0"
                value={bulkQuantity}
                onChange={(e) => setBulkQuantity(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-900 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500 transition-colors"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowBulkResetModal(false)}
                className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmBulkReset}
                disabled={loading}
                className="rounded-xl bg-red-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {loading ? "Resetting..." : "Confirm Bulk Reset"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info Modal */}
      {infoModalStatus && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-slate-900">
                {infoModalStatus === "all" && "All Items"}
                {infoModalStatus === "low" && "Items Running Low (< 5)"}
                {infoModalStatus === "out" && "Items Out of Stock (<= 0)"}
                {infoModalStatus === "expiring" &&
                  "Items Expiring Soon (<= 3 months)"}
              </h3>
              <button
                onClick={() => setInfoModalStatus(null)}
                className="p-2 sm:p-0 text-2xl sm:text-base text-slate-400 hover:text-slate-600 font-bold"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-auto rounded-xl border border-slate-200">
              {(() => {
                const modalItems = items.filter((item) => {
                  if (infoModalStatus === "low")
                    return (
                      item.remaining_quantity > 0 && item.remaining_quantity < 5
                    );
                  if (infoModalStatus === "out")
                    return item.remaining_quantity <= 0;
                  if (infoModalStatus === "expiring")
                    return (
                      item.remaining_quantity > 0 &&
                      item.expiry_date &&
                      new Date(item.expiry_date) <= threeMonthsFromNow
                    );
                  return true;
                });

                if (modalItems.length === 0) {
                  return (
                    <div className="p-8 text-center text-slate-500">
                      No items found
                    </div>
                  );
                }

                return (
                  <ul className="divide-y divide-slate-100">
                    {modalItems.map((item) => (
                      <li
                        key={item.id}
                        className="flex justify-between p-3 hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex flex-col">
                          <span className="font-medium text-slate-700">
                            {item.item_name}
                          </span>
                          {infoModalStatus === "expiring" &&
                            item.expiry_date && (
                              <span className="text-xs text-purple-600 mt-1 font-semibold">
                                Exp:{" "}
                                {new Date(item.expiry_date).toLocaleDateString(
                                  "en-GB",
                                  {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                  },
                                )}
                              </span>
                            )}
                        </div>
                        <span
                          className={`font-bold ${item.remaining_quantity <= 0 ? "text-red-600" : item.remaining_quantity < 5 ? "text-orange-600" : "text-blue-600"}`}
                        >
                          {item.remaining_quantity} left
                        </span>
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Add Ingredient Modal */}
      {showAddIngredientModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="mb-2 text-lg font-bold text-slate-900">
              Add Ingredient
            </h3>
            <p className="mb-6 text-sm text-slate-500">
              Add a new ingredient to the inventory.
            </p>
            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={ingredientForm.name}
                onChange={(e) =>
                  setIngredientForm({ ...ingredientForm, name: e.target.value })
                }
                placeholder="e.g. Tomato Paste"
                className="w-full rounded-xl border border-slate-300 px-4 py-2 text-slate-900 focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
              />
            </div>
            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Quantity
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={ingredientForm.quantity}
                onChange={(e) =>
                  setIngredientForm({
                    ...ingredientForm,
                    quantity: e.target.value,
                  })
                }
                placeholder="0"
                className="w-full rounded-xl border border-slate-300 px-4 py-2 text-slate-900 focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
              />
            </div>
            <div className="mb-8">
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Unit <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={ingredientForm.unit}
                onChange={(e) =>
                  setIngredientForm({ ...ingredientForm, unit: e.target.value })
                }
                placeholder="e.g. bags, boxes, cartons, packs"
                className="w-full rounded-xl border border-slate-300 px-4 py-2 text-slate-900 focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
              />
              <p className="mt-1.5 text-xs text-slate-400">
                Enter any unit — bags, boxes, cartons, packs, etc.
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowAddIngredientModal(false);
                  setIngredientForm({ name: "", quantity: "", unit: "" });
                }}
                className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddIngredient}
                disabled={submittingIngredient}
                className="rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50 transition-colors"
              >
                {submittingIngredient ? "Adding..." : "Add Ingredient"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Ingredient Modal */}
      {showEditIngredientModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="mb-6 text-lg font-bold text-slate-900">Edit Ingredient</h3>
            <div className="mb-5">
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={editIngredientForm.name}
                onChange={(e) =>
                  setEditIngredientForm({ ...editIngredientForm, name: e.target.value })
                }
                className="w-full rounded-xl border border-slate-300 px-4 py-2 text-slate-900 focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
              />
            </div>
            <div className="mb-8">
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Unit <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={editIngredientForm.unit}
                onChange={(e) =>
                  setEditIngredientForm({ ...editIngredientForm, unit: e.target.value })
                }
                placeholder="e.g. bags, boxes, cartons, packs"
                className="w-full rounded-xl border border-slate-300 px-4 py-2 text-slate-900 focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowEditIngredientModal(false);
                  setEditIngredientForm({ id: "", name: "", unit: "" });
                }}
                className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEditIngredientModalSubmit}
                disabled={submitting}
                className="rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50 transition-colors"
              >
                {submitting ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Ingredient Stock Modal */}
      {addIngredientStockItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="mb-2 text-lg font-bold text-slate-900">Add Ingredient Stock</h3>
            <p className="mb-6 text-sm text-slate-500">
              Add new stock for <strong className="text-slate-800">{addIngredientStockItem.name}</strong>.
            </p>
            <div className="mb-8">
              <label className="mb-2 block text-sm font-medium text-slate-700 font-semibold">
                Quantity to Add/Remove (use - to reduce)
              </label>
              <input
                type="number"
                value={addIngredientStockQuantity}
                onChange={(e) => setAddIngredientStockQuantity(e.target.value)}
                placeholder="0"
                className="mt-1 w-full rounded-xl border border-slate-300 px-4 py-2 text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
              <div className="mt-4 p-4 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-between">
                <span className="text-sm font-semibold text-emerald-800">New Total Stock:</span>
                <span className="text-2xl font-black text-emerald-600">
                  {Math.floor(addIngredientStockItem.total_stock + (parseFloat(addIngredientStockQuantity) || 0))} {addIngredientStockItem.unit}
                </span>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setAddIngredientStockItem(null)}
                className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddIngredientStock}
                disabled={submittingAddIngredientStock}
                className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              >
                {submittingAddIngredientStock ? "Adding..." : "Add Stock"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Ingredient Modal */}
      {resetIngredientItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="mb-2 text-lg font-bold text-slate-900">Reset Ingredient</h3>
            <p className="mb-6 text-sm text-slate-500">
              Reset stock counts for <strong className="text-slate-800">{resetIngredientItem.name}</strong>. Enter new starting stock.
            </p>
            <div className="mb-8">
              <label className="mb-2 block text-sm font-medium text-slate-700">
                New Starting Quantity ({resetIngredientItem.unit})
              </label>
              <input
                type="number"
                min="0"
                value={resetIngredientNewQty}
                onChange={(e) => setResetIngredientNewQty(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-900 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500 transition-colors"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setResetIngredientItem(null)}
                className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleResetIngredient}
                disabled={submittingResetIngredient}
                className="rounded-xl bg-red-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {submittingResetIngredient ? "Resetting..." : "Confirm Reset"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Ingredient Modal */}
      {deleteIngredientItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="mb-2 text-lg font-bold text-slate-900">Delete Ingredient</h3>
            <p className="mb-6 text-sm text-slate-500">
              Are you sure you want to permanently delete <strong className="text-slate-800">{deleteIngredientItem.name}</strong>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteIngredientItem(null)}
                className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteIngredient}
                disabled={submittingDeleteIngredient}
                className="rounded-xl bg-red-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {submittingDeleteIngredient ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {error && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <svg
                className="h-6 w-6 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h3 className="mb-2 text-lg font-bold text-slate-900">
              Action Failed
            </h3>
            <p className="mb-6 text-sm text-slate-500">{error}</p>
            <button
              onClick={() => setError(null)}
              className="w-full rounded-xl bg-red-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-red-700 transition-colors"
            >
              Okay, got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryTab;
